import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Button, notification, Space, Modal, Tag, Typography, Spin, Badge, Tabs, Alert } from 'antd';
import { ArrowLeftOutlined, SettingOutlined, UsergroupAddOutlined, ShareAltOutlined, LoadingOutlined, CheckCircleTwoTone, CloseCircleTwoTone, ExclamationCircleOutlined, TeamOutlined, BankOutlined, MailOutlined, ExportOutlined } from '@ant-design/icons';
import OrgCareProgramsPanel from '../../components/CarePrograms/OrgCareProgramsPanel';
import lib, { myRequestObj } from "../../assets/utils/lib";
import './org.css';
import './orgs-dashboard.css';
import DashboardHero from '../dashboard/components/DashboardHero';
import { hasMetricValue } from '../dashboard/utils';
import { useAuth } from "../../assets/utils/useAuth";
import { usePermissions } from "../../assets/utils/usePermissions";
import { resolveOrgActionAccess } from "./orgActionPermissions";
import Helpers, { downloadFile } from "../../assets/utils/Helpers";
import Register, { ListOfDependent } from "../compoinents/profileUpdate";
import EmailVerificationModal from "../compoinents/emailVerificationModal";
import Dropdown from "../compoinents/DroptDown";
import PlanRequestsPanel from "./components/PlanRequestsPanel";
import OrgMembersPanel from "./components/OrgMembersPanel";
import OrgAccountAdmin from "./components/OrgAccountAdmin";
import { canManagePlanRequests, canManageStaffUploads, canManageOrgPlans as canManageOrgPlansFor } from "./planRequestHelpers";
import { canViewBulkUploadLogs } from "../../components/CarePrograms/careProgramPermissions";
import ExportPanel from "./components/ExportPanel";
import OrgPaymentAdviceExportPanel from "../../components/Organizations/OrgPaymentAdviceExportPanel";
import ClaimsExportsDrawer from "../../components/Claims/ClaimsExportsDrawer";
import { usePolicies, POLICY_DATA_TABS } from "./hooks/usePolicies";
import {
  getPendingOnboardingCount,
  membersSectionToPolicyTab,
  resolveOrgDetailRoute,
} from "./orgDetailTabHelpers";
import { uri } from "../../assets/utils/http-request";
const { Text } = Typography;

const OrganizationDetail = props => {
  const { org } = useParams()
  const { user } = useAuth();
  const { hasAnyPermission, canStayOnModule } = usePermissions();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = searchParams.get('tab');
  const sectionFromUrl = searchParams.get('section');

  const canReviewPlanRequests = useMemo(
    () => canManagePlanRequests(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  const canReviewStaffUploads = useMemo(
    () => canManageStaffUploads(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  const canSeeUploadHistory = useMemo(
    () => canViewBulkUploadLogs(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  const showUploadsSection = canReviewStaffUploads || canSeeUploadHistory;

  const initialRoute = useMemo(
    () => resolveOrgDetailRoute(searchParams),
    [searchParams],
  );

  const [orgRecord, setOrgRecord] = useState(locationState || null);
  const [orgLoading, setOrgLoading] = useState(!locationState && Boolean(org));

  const {
    setActiveTab: setPoliciesTab,
    data: policiesData,
    tabCounts,
    pagination,
    search: policiesSearch,
    setSearch: setPoliciesSearch,
    loading: policiesLoading,
    exportPolicies,
    goToPage,
    refetch: refetchPolicies
  } = usePolicies(org, membersSectionToPolicyTab(initialRoute.section || 'active') || 'active');

  const [focusData, setFocusData] = useState(null)
  const [showReg, setShowReg] = useState(false)
  const [showEnrolless, setShowEnrollees] = useState(false)
  const [loadingEnrollees, setLoadingEnrollees] = useState(false)
  const [dependentList, setDependentList] = useState([]);

  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);  const [loadingDownloadAction, setLoadingDownloadAction] = useState(false);
  const [sharingActionPayload, setSharingActionPayload] = useState();
  const [activeTab, setActiveTab] = useState(initialRoute.tab);
  const [membersSection, setMembersSection] = useState(initialRoute.section || 'active');
  const [exportsDrawerOpen, setExportsDrawerOpen] = useState(false);
  const [pendingPlanRequestsCount, setPendingPlanRequestsCount] = useState(0);
  const [pendingStaffUploadsCount, setPendingStaffUploadsCount] = useState(0);

  // Manage plans is its own capability now, not a by-product of being able to
  // review plan requests.
  const canManageOrgPlans = useMemo(
    () => canManageOrgPlansFor(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  // Edit / renew / disable / set-password / share each have their own gate,
  // mirroring the endpoint behind them. Previously only Manage plans was
  // checked and the rest rendered for anyone who could open the screen.
  const orgActions = useMemo(
    () => resolveOrgActionAccess(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  useEffect(() => {
    if (activeTab === 'plan_requests' && !canReviewPlanRequests) {
      setActiveTab('members');
      setMembersSection('active');
      setSearchParams({ tab: 'members', section: 'active' }, { replace: true });
    }
  }, [activeTab, canReviewPlanRequests, setSearchParams]);

  useEffect(() => {
    if (membersSection === 'uploads' && !showUploadsSection) {
      setMembersSection('active');
      setSearchParams({ tab: 'members', section: 'active' }, { replace: true });
    }
  }, [membersSection, showUploadsSection, setSearchParams]);

  useEffect(() => {
    if (!orgRecord?.org_id || !canReviewPlanRequests) {
      setPendingPlanRequestsCount(0);
      return;
    }
    lib.getPendingPlanRequestsCount(orgRecord.org_id).then(setPendingPlanRequestsCount);
  }, [orgRecord?.org_id, canReviewPlanRequests, activeTab]);

  useEffect(() => {
    if (!orgRecord?.org_id || !canReviewStaffUploads) {
      setPendingStaffUploadsCount(0);
      return;
    }
    lib.getPendingStaffUploadsCount(orgRecord.org_id).then(setPendingStaffUploadsCount);
  }, [orgRecord?.org_id, canReviewStaffUploads, activeTab]);

  // Friendly date formatter: e.g., "Sep 24, 2025"; handles unset/invalid values
  const formatDate = (value, fallback = 'Not set') => {
    if (!value) return fallback;
    if (typeof value === 'string' && value.startsWith('0')) return fallback;
    const dateObj = new Date(value);
    if (Number.isNaN(dateObj.getTime())) return fallback;
    return dateObj.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!canStayOnModule('organizations', [1, 3, 4, 5, 6])) {
      notification.warning({
        message: 'Access Denied',
        description: "You don't have permission to view this organization.",
        placement: 'topLeft',
      });
      navigate('/corporations');
    }
  }, [user, canStayOnModule, navigate]);

  useEffect(() => {
    if (!org) {
      navigate('/corporations');
      return;
    }
    if (locationState?.org_id || locationState?.org_social_no) {
      setOrgRecord(locationState);
      setOrgLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setOrgLoading(true);
      try {
        const row = await lib.getOrganizationByHash(org);
        if (cancelled) return;
        if (!row?.org_id && !row?.org_social_no) {
          notification.error({ message: 'Organization not found' });
          navigate('/corporations');
          return;
        }
        setOrgRecord(row);
      } catch (e) {
        if (!cancelled) {
          notification.error({ message: e?.message || 'Failed to load organization' });
          navigate('/corporations');
        }
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [org, locationState, navigate]);

  // Enrich with enrollment segment resolution / override options from detail endpoint.
  useEffect(() => {
    const socialNo = orgRecord?.org_social_no;
    if (!socialNo) return undefined;
    if (orgRecord?.enrollment_segment_resolution && orgRecord?.enrollment_segment_options) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const parsed = await lib.getCorporation(socialNo);
      if (cancelled || !parsed.ok || !parsed.data) return;
      setOrgRecord((prev) => {
        if (!prev || String(prev.org_social_no) !== String(socialNo)) return prev;
        return {
          ...prev,
          ...parsed.data,
          enrollment_segment_options: parsed.data.enrollment_segment_options
            || parsed.enrollment_segment_options
            || prev.enrollment_segment_options,
          enrollment_segment_taxonomy: parsed.data.enrollment_segment_taxonomy
            || parsed.enrollment_segment_taxonomy
            || prev.enrollment_segment_taxonomy,
          enrollment_segment_resolution: parsed.data.enrollment_segment_resolution
            || parsed.enrollment_segment_resolution
            || prev.enrollment_segment_resolution,
        };
      });
    })();
    return () => { cancelled = true; };
  }, [orgRecord?.org_social_no]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync UI from URL when tab/section query changes (legacy tab keys redirect to members).
  useEffect(() => {
    const route = resolveOrgDetailRoute(
      new URLSearchParams([
        ...(tabFromUrl ? [['tab', tabFromUrl]] : []),
        ...(sectionFromUrl ? [['section', sectionFromUrl]] : []),
      ]),
    );
    if (route.needsRedirect) {
      setSearchParams(route.params, { replace: true });
      return;
    }
    setActiveTab(route.tab);
    if (route.tab === 'members') {
      setMembersSection(route.section);
      const policyTab = membersSectionToPolicyTab(route.section);
      if (policyTab && POLICY_DATA_TABS.includes(policyTab)) {
        setPoliciesTab(policyTab);
      }
    }
  }, [tabFromUrl, sectionFromUrl, setSearchParams, setPoliciesTab]);

  const handleMembersSectionChange = useCallback((section) => {
    setMembersSection(section);
    const policyTab = membersSectionToPolicyTab(section);
    if (policyTab && POLICY_DATA_TABS.includes(policyTab)) {
      setPoliciesTab(policyTab);
    }
    setSearchParams({ tab: 'members', section }, { replace: true });
  }, [setPoliciesTab, setSearchParams]);

  const downloadOrgData = async (payload) => {
    setLoadingDownloadAction(true);

    notification.info({
      icon: <LoadingOutlined spin />,
      message: <>{payload?.target?.toUpperCase()} DATA IS BEING PROCESSED</>,
      key: 'download',
    });

    payload = { ...payload, social_no: org };

    try {
      const { data } = await myRequestObj('downloadOrgUsersData').post(payload);
      if (Number(data?.error) === 0) {
        notification.info({
          icon: <CheckCircleTwoTone twoToneColor="green" />,
          message: (
            <>
              <strong>{data?.message}</strong><br /><br />
              The record has been successfully processed.<br />
              {payload?.purpose === 'get' && <>Download will be processed in a moment.<br /><br /></>}
              <strong>You do not need to reinitiate this process</strong>
            </>
          ),
          key: 'download',
        });

        if (payload?.purpose === 'get') {
          const fileUrl = atob(data?.data);
          try {
            await downloadFile(uri + fileUrl, fileUrl.split('/')[2]);
            notification.info({
              icon: <CheckCircleTwoTone twoToneColor='green' />,
              message: "Download completed successfully",
              key: 'download'
            });
          } catch (error) {
            notification.info({
              icon: <CloseCircleTwoTone twoToneColor="red" />,
              message: (
                <>
                  <strong>Download Failed</strong><br /><br /> {error.message || error}
                </>
              ),
              key: 'download',
            });
          }
        }
        setIsEmailModalVisible(false);
      } else {
        notification.info({
          icon: <CloseCircleTwoTone twoToneColor="red" />,
          message: data?.message,
          key: 'download',
        });
      }
    } catch (e) {
      notification.info({
        icon: <CloseCircleTwoTone twoToneColor="red" />,
        message: e?.msg || e?.message,
        key: 'download',
      });
    } finally {
      notification.destroy('download');
      setLoadingDownloadAction(false);
    }
  };

  const handleView = (record) => {
    setFocusData(record)
    setShowReg(true)
  };

  const handleViewEnrollees = (record) => {
    setFocusData(record)
    setShowEnrollees(true)
  };

  const handleCancel = () => {
    setShowReg(false)
    setFocusData(null)
  }

  const handleCancelEnrollees = () => {
    setShowEnrollees(false)
    setFocusData(null)
    setDependentList([]);
  }

  const policiesColumns = useMemo(() => [
    {
      title: 'Policy',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <Text strong>{name || '—'}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{record?.policy_no || '—'}</Text></div>
        </div>
      ),
    },
    {
      title: 'Plan',
      key: 'premium',
      render: (_, record) => (
        <div>
          <Text>{record?.insurance_package || '—'}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{record?.premium_type || '—'}</Text></div>
        </div>
      ),
    },
    {
      title: 'Enrollees',
      key: 'enrollees',
      width: 110,
      render: (_, record) => (
        <Text>{record?.active_enrollees ?? 0} / {record?.enrolee_size ?? 0}</Text>
      ),
    },
    {
      title: 'Joined',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: 'Renewal',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Tag color={record.status ? 'success' : 'default'}>
          {record.status ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 88,
      render: (_, record) => (
        <Space size={4}>
          <Button type="text" size="small" icon={<SettingOutlined />} onClick={() => handleView(record)} aria-label="Edit policy" />
          <Button type="text" size="small" icon={<UsergroupAddOutlined />} onClick={() => handleViewEnrollees(record)} aria-label="View enrollees" />
        </Space>
      ),
    },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateUpdatedUserData = () => {
    refetchPolicies();
  }

  const handleOpenEmailModal = (payload) => {    setSharingActionPayload(payload)
    setIsEmailModalVisible(true);
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalVisible(false);
    setSharingActionPayload(null)
  };

  const handleEmailSuccess = async (email) => {
    setSharingActionPayload({ ...sharingActionPayload, email: email + '@amanhmo.com' })
    confirm({ ...sharingActionPayload, email: email + '@amanhmo.com' })
  };

  const confirm = (payload) => {
    Modal.confirm({
      title: `Share org ${payload?.target || 'data'}`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          <p>Send to <strong>{payload?.email}</strong></p>
          <p style={{ color: '#cf1322', marginBottom: 0 }}>
            Verify the email is correct before proceeding. You will not get feedback if it is wrong.
          </p>
        </>
      ),
      okText: 'Send',
      onOk: () => downloadOrgData(payload),
      cancelText: 'Cancel',
    });
  };
  const dataDwonloadMenuItems = [
    // {
    //   key: '1',
    //   label: 'Download Policies',
    //   onClick: () => {
    //     downloadOrgData({ target: 'policies', purpose: 'get' });
    //   }
    // },
    {
      key: '3',
      label: 'Share Policies List',
      onClick: () => {
        handleOpenEmailModal({ target: 'policies', purpose: 'share' });
      }
    },
    // {
    //   key: '2',
    //   label: 'Download Enrollees',
    //   onClick: () => {
    //     downloadOrgData({ target: 'enrollees', purpose: 'get' });
    //   }
    // },
    {
      key: '4',
      label: 'Share Enrollee List',
      onClick: () => {
        handleOpenEmailModal({ target: 'enrollees', purpose: 'share' });
      }
    },
    {
      key: '5',
      label: 'Share Both Lists',
      onClick: () => {
        handleOpenEmailModal({ target: 'policies and enrollees', purpose: 'share' });
      }
    },
  ];

  const isOrgActive = parseInt(orgRecord?.org_status, 10) === 1;

  const heroMetrics = useMemo(() => {
    if (!orgRecord || orgRecord?.tag === 'self_payer') return [];
    return [
      {
        key: 'policies',
        title: 'Active policies',
        value: tabCounts.active?.count ?? orgRecord?.active_policies,
        color: '#49B870',
        icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
        subtitle: 'People with an active membership',
      },
      {
        key: 'enrollees',
        title: 'Active enrollees',
        value: orgRecord?.active_enrollees,
        color: '#3497D3',
        icon: <TeamOutlined />,
        subtitle: 'Organization total',
      },
      {
        key: 'pending',
        title: 'Pending onboarding',
        value: getPendingOnboardingCount(tabCounts),
        color: '#722ed1',
        icon: <TeamOutlined />,
        subtitle: 'Waiting to finish signup',
      },
      {
        key: 'all_policies',
        title: 'All policies',
        value: orgRecord?.all_policies,
        color: '#94a3b8',
        icon: <BankOutlined />,
        subtitle: 'Including inactive',
      },
    ].filter((m) => hasMetricValue(m.value));
  }, [orgRecord, tabCounts]);

  return (
    <div className="orgs-dashboard orgs-detail-page">
      <Spin spinning={orgLoading} tip="Loading organization...">
        <div className="dashboard-page-stack">
          <header className="dash-masthead">
            <div className="dash-masthead__lead">
              <p className="org-detail-breadcrumb">
                <Button type="link" className="org-detail-breadcrumb__link" onClick={() => navigate('/corporations')}>
                  Organizations
                </Button>
                <span aria-hidden>/</span>
                <span>{orgRecord?.org_name || 'Detail'}</span>
              </p>
              <h1 className="dash-masthead__title">{orgRecord?.org_name || 'Organization detail'}</h1>
              <p className="dash-masthead__meta">
                {orgRecord?.org_code || orgRecord?.org_social_no || '—'}
                {orgRecord ? (
                  <>
                    <span className="dash-masthead__sep" aria-hidden>·</span>
                    {[orgRecord?.org_city, orgRecord?.org_state].filter(Boolean).join(', ') || '—'}
                    <span className="dash-masthead__sep" aria-hidden>·</span>
                    <Tag color={isOrgActive ? 'success' : 'default'} style={{ margin: 0 }}>
                      {isOrgActive ? 'Active' : 'Inactive'}
                    </Tag>
                    {(orgRecord?.enrollment_segment_label || orgRecord?.enrollment_segment) ? (
                      <>
                        <span className="dash-masthead__sep" aria-hidden>·</span>
                        <Tag color="geekblue" style={{ margin: 0 }}>
                          {orgRecord.enrollment_segment_label
                            || String(orgRecord.enrollment_segment).toUpperCase()}
                        </Tag>
                      </>
                    ) : null}
                  </>
                ) : null}
              </p>
            </div>
            <div className="dash-masthead__actions">
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/corporations')}>Back</Button>
              {orgRecord && orgRecord?.tag !== 'self_payer' ? (
                <OrgAccountAdmin
                  organization={orgRecord}
                  canManagePlans={canManageOrgPlans}
                  canEdit={orgActions.canEdit}
                  canSetStatus={orgActions.canSetStatus}
                  canRenew={orgActions.canRenew}
                  canSetPassword={orgActions.canSetPassword}
                  compact
                  onOrganizationChange={(next) => setOrgRecord((prev) => ({ ...prev, ...next }))}
                />
              ) : null}
              {orgActions.canShareRosters ? (
                <>
                  <Button icon={<ExportOutlined />} onClick={() => setExportsDrawerOpen(true)}>Report downloads</Button>
                  {/* Emails the organisation's full policy/enrollee roster to an
                      arbitrary address — same gate as the export endpoint. */}
                  <Dropdown menuItems={dataDwonloadMenuItems} icon={loadingDownloadAction ? <LoadingOutlined spin /> : <ShareAltOutlined />} textContent="Share" disabled={loadingDownloadAction} />
                </>
              ) : null}
            </div>
          </header>

          {orgRecord && orgRecord?.tag !== 'self_payer' ? (
            <div className="org-detail-context-strip">
              <div className="org-detail-chip">
                <span className="org-detail-chip__label">Member since</span>
                <span className="org-detail-chip__value">{formatDate(orgRecord?.org_joined_date)}</span>
              </div>
              <div className="org-detail-chip">
                <span className="org-detail-chip__label">Renewal</span>
                <span className="org-detail-chip__value">{formatDate(orgRecord?.org_expiry_date)}</span>
              </div>
              {orgRecord?.org_email ? (
                <div className="org-detail-chip">
                  <span className="org-detail-chip__label">Email</span>
                  <span className="org-detail-chip__value">
                    <MailOutlined style={{ marginRight: 4 }} />
                    {orgRecord.org_email}
                  </span>
                </div>
              ) : null}
              {orgRecord?.enrollment_segment_resolution?.effective_source ? (
                <div className="org-detail-chip">
                  <span className="org-detail-chip__label">Segment source</span>
                  <span className="org-detail-chip__value">
                    {orgRecord.enrollment_segment_resolution.effective_source === 'override'
                      ? 'Manual override'
                      : 'Locked until renewal'}
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          {orgRecord?.enrollment_segment_resolution?.pending_reclassification ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Will become ${orgRecord.enrollment_segment_resolution.pending_segment || 'a new segment'} at next renewal`}
              description={orgRecord.enrollment_segment_resolution.cycle_boundary_note
                || 'Computed segment applies at the next policy cycle renewal unless overridden on Edit organization.'}
            />
          ) : null}

          {heroMetrics.length > 0 ? (
            <section className="dash-overview dash-overview--compact" aria-label="Organization summary">
              <DashboardHero metrics={heroMetrics} loading={orgLoading && !orgRecord} />
            </section>
          ) : null}

          <section className="org-detail-panel org-detail-panel--flush">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                if (key === 'members') {
                  setSearchParams({ tab: 'members', section: membersSection }, { replace: true });
                } else {
                  setSearchParams({ tab: key }, { replace: true });
                }
              }}
              className="org-detail-tabs"
              items={[
                {
                  key: 'members',
                  label: 'Members',
                  children: (
                    <OrgMembersPanel
                      section={membersSection}
                      onSectionChange={handleMembersSectionChange}
                      tabCounts={tabCounts}
                      pendingStaffUploadsCount={pendingStaffUploadsCount}
                      showUploadsSection={showUploadsSection}
                      canReviewStaffUploads={canReviewStaffUploads}
                      canSeeUploadHistory={canSeeUploadHistory}
                      orgRecord={orgRecord}
                      socialNo={org}
                      onPendingCountChange={setPendingStaffUploadsCount}
                      policiesColumns={policiesColumns}
                      policiesData={policiesData}
                      policiesSearch={policiesSearch}
                      setPoliciesSearch={setPoliciesSearch}
                      policiesLoading={policiesLoading}
                      refetchPolicies={refetchPolicies}
                      pagination={pagination}
                      goToPage={goToPage}
                    />
                  ),
                },
                ...(canReviewPlanRequests ? [{
                  key: 'plan_requests',
                  label: (
                    <span className={`dash-tab-label${pendingPlanRequestsCount > 0 ? ' dash-tab-label--attention' : ''}`}>
                      <span>Plan requests</span>
                      {pendingPlanRequestsCount > 0 ? (
                        <Badge
                          count={pendingPlanRequestsCount}
                          size="small"
                          overflowCount={99}
                          className="dash-tab-badge"
                        />
                      ) : null}
                    </span>
                  ),
                  children: (
                    <PlanRequestsPanel
                      embedded
                      scopedOrgId={orgRecord?.org_id}
                      orgName={orgRecord?.org_name}
                      onPendingCountChange={setPendingPlanRequestsCount}
                    />
                  ),
                }] : []),
                {
                  key: 'care_programs',
                  label: 'Care programs',
                  children: (
                    <OrgCareProgramsPanel orgId={orgRecord?.org_id} orgName={orgRecord?.org_name} />
                  ),
                },
                {
                  key: 'payment_advice',
                  label: 'Utilization reports',
                  children: (
                    <OrgPaymentAdviceExportPanel
                      organization={orgRecord}
                      user={user}
                      onOpenExportsDrawer={() => setExportsDrawerOpen(true)}
                    />
                  ),
                },
                {
                  key: 'export',
                  label: `Export (${tabCounts.export?.count || 0})`,
                  children: (
                    <ExportPanel
                      tabCounts={tabCounts}
                      onExport={async (exportTab) => {
                        const result = await exportPolicies(exportTab, policiesSearch);
                        if (result?.download_url) {
                          try {
                            const filename = result.file || result.filename || null;
                            await lib.downloadFileWithAuth(result.download_url, filename);
                            notification.success({
                              message: 'Download started',
                              placement: 'topRight',
                            });
                          } catch (error) {
                            notification.error({
                              message: 'Download failed',
                              description: error?.message || 'Please try again.',
                              placement: 'topRight',
                            });
                          }
                        } else {
                          notification.warning({
                            message: 'No download URL',
                            description: 'The export response did not include a download URL',
                            placement: 'topRight',
                          });
                        }
                      }}
                      loading={policiesLoading}
                      searchApplied={!!policiesSearch}
                    />
                  ),
                },
              ]}
            />
          </section>
        </div>
      </Spin>      {showReg && <Register user={focusData} orgSocialNo={orgRecord?.org_social_no} handleCancel={handleCancel} isModalVisible={showReg} command={updateUpdatedUserData} />}
      {showEnrolless && <ListOfDependent dependentList={dependentList} policy={focusData} showModal={showEnrolless} closeModal={handleCancelEnrollees} loading={loadingEnrollees} viewOnly={true} />}
      {isEmailModalVisible && (<EmailVerificationModal domain="amanhmo.com" onSuccess={handleEmailSuccess} onCancel={handleCloseEmailModal} payload={sharingActionPayload} title="Orgnizaion Data Request" />)}
      <ClaimsExportsDrawer open={exportsDrawerOpen} onClose={() => setExportsDrawerOpen(false)} />
    </div>
  )
}

export default OrganizationDetail;